export enum ProcessingStatus {
  PROCESSING = "PROCESSING",
  COMPLETE = "COMPLETE",
  ERROR = "ERROR",
}

export enum Difficulty {
  BEGINNER = "BEGINNER",
  INTERMEDIATE = "INTERMEDIATE",
  ADVANCED = "ADVANCED",
}

export interface WorkoutExerciseItemInput {
  exercise_id: string;
  order: number;
}

export interface CreateTraditionalExerciseRequest {
  title: string;
  original_video_storage_key?: string;
  processed_video_storage_key?: string;
  video_processing_status?: ProcessingStatus;
  video_duration?: number; // Raw video duration in seconds (with decimals)
  exercise_duration?: number;
  sets: number;
  reps?: string; // Can be a single number like "8" or a range like "8-12"
  set_duration?: number; // Duration of each set in seconds (for interval-based exercises)
  rest_between_sets: number;
  notes?: string;
}

export interface UpdateTraditionalExerciseRequest {
  title?: string;
  original_video_storage_key?: string;
  processed_video_storage_key?: string;
  video_processing_status?: ProcessingStatus;
  video_duration?: number; // Raw video duration in seconds (with decimals)
  exercise_duration?: number;
  sets?: number;
  reps?: string | null; // Can be a single number like "8" or a range like "8-12", null to clear
  set_duration?: number | null; // Duration of each set in seconds (for interval-based exercises), null to clear
  rest_between_sets?: number;
  notes?: string;
}

export interface CreateTagRequest {
  name: string;
}

export interface UpdateTagRequest {
  name: string;
}

export interface CreateWorkoutRequest {
  title: string;
  thumbnail_image_storage_key?: string;
  trainer_id?: number;
  difficulty: Difficulty;
  duration: number; // Duration in SECONDS (admin UI converts from minutes before sending)
  rounds?: number;
  tag_ids?: string[];
  exercises: WorkoutExerciseItemInput[];
  is_exclusive?: boolean;
}

export interface UpdateWorkoutRequest {
  title?: string;
  thumbnail_image_storage_key?: string;
  trainer_id?: number | null;
  difficulty?: Difficulty;
  duration?: number; // Duration in SECONDS (admin UI converts from minutes before sending)
  rounds?: number;
  tag_ids?: string[];
  exercises?: WorkoutExerciseItemInput[];
  is_exclusive?: boolean;
}

export interface TrainerResponse {
  id: number;
  title: string;
  slug: string;
}

export interface TagResponse {
  id: string;
  name: string;
}

export interface TraditionalExerciseResponse {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  original_video_storage_key: string | null;
  original_video_url: string | null;
  processed_video_storage_key: string | null;
  processed_video_url: string | null;
  video_thumbnail_storage_key: string | null;
  video_thumbnail_url: string | null;
  video_processing_status: ProcessingStatus | null;
  video_duration: number | null; // Raw video duration in seconds (with decimals)
  exercise_duration: number | null;
  sets: number;
  reps: string | null; // Can be a single number like "8" or a range like "8-12"
  set_duration: number | null; // Duration of each set in seconds (for interval-based exercises)
  rest_between_sets: number;
  notes: string | null;
}

export interface WorkoutExerciseItemResponse {
  id: string;
  order: number;
  exercise: TraditionalExerciseResponse;
}

export interface WorkoutListItemResponse {
  id: string;
  created_at: string;
  updated_at: string;
  processing_status: ProcessingStatus;
  title: string;
  thumbnail_image_storage_key: string | null;
  thumbnail_image_url?: string;
  trainer: TrainerResponse | null;
  difficulty: Difficulty;
  duration: number; // Duration in SECONDS from API (admin UI converts to minutes for display)
  rounds: number;
  tags: TagResponse[];
  is_exclusive: boolean;
}

export interface WorkoutDetailResponse extends WorkoutListItemResponse {
  exercises: WorkoutExerciseItemResponse[];
}

export interface ApiBaseResponse {
  message: string;
  error: boolean;
}

export type ApiResponse<T> = ApiBaseResponse & { results: T | null };

export type ApiListResponse<T> = ApiBaseResponse & { results: T[] | null };

export type ApiNoResultsResponse = ApiBaseResponse;

export type CreateExerciseResponse = ApiResponse<TraditionalExerciseResponse>;
export type ListExercisesResponse =
  ApiListResponse<TraditionalExerciseResponse>;
export type UpdateExerciseResponse = ApiResponse<TraditionalExerciseResponse>;
export type DeleteExerciseResponse = ApiNoResultsResponse;

export type CreateTagResponse = ApiResponse<TagResponse>;
export type ListTagsResponse = ApiListResponse<TagResponse>;
export type UpdateTagResponse = ApiResponse<TagResponse>;
export type DeleteTagResponse = ApiNoResultsResponse;

export type CreateWorkoutResponse = ApiResponse<WorkoutDetailResponse>;
export type ListWorkoutsResponse = ApiListResponse<WorkoutListItemResponse>;
export type GetWorkoutResponse = ApiResponse<WorkoutDetailResponse>;
export type UpdateWorkoutResponse = ApiResponse<WorkoutDetailResponse>;
export type DeleteWorkoutResponse = ApiNoResultsResponse;

// File Upload Types
export interface GenerateFileUploadUrlRequest {
  filename: string;
  content_type: string;
}

export interface FileUploadUrlResponse {
  upload_url: string;
  storage_key: string;
  file_id: string;
}

export type GenerateFileUploadUrlResponse = ApiResponse<FileUploadUrlResponse>;
